/**
 * Every gauge state on one page.
 *
 * Overdrawn, nearly-empty, and just-rolled-over are the states that matter most
 * and are the most annoying to reach by hand in a real family's data. A state
 * you can't look at is a state you ship broken.
 *
 * Values are built through the real domain module, not hand-written, so this
 * page also fails if `projectEnvelope` starts disagreeing with the gauge.
 */

import { useState } from 'react';
import FuelGauge from '../ui/FuelGauge.jsx';
import QuickLog from '../ui/QuickLog.jsx';
import { FuelSetupForm } from './FuelSetup.jsx';
import {
  envelopeReversalEntry, projectEnvelope, refillEntries, spendEntry,
} from '../domain/envelope.js';

const FAMILY = 'preview-family';
const ENVELOPE = 'preview-envelope';
const RULE = { version: 'fortunaut-envelope-v1', cadence: 'weekly', amountCents: 6000, rolloverCapCents: 4000 };
const TODAY = '2026-08-18';

/** Build a real projection from real entries, the way the app will. */
function scenario(spends, extraRefills = false) {
  const entries = refillEntries({
    familyId: FAMILY, envelopeId: ENVELOPE, rule: RULE,
    fromDate: extraRefills ? '2026-08-02' : '2026-08-16', toDate: TODAY,
  });
  spends.forEach((amountCents, i) => {
    entries.push(spendEntry({
      familyId: FAMILY, envelopeId: ENVELOPE, spendId: `s${i}`,
      amountCents, occurredAt: TODAY,
    }));
  });
  return projectEnvelope(entries);
}

const GOAL = { name: 'Beach trip', savedCents: 8400, targetCents: 30000 };

const CASES = [
  { label: 'Full tank, just refilled', spends: [] },
  { label: 'Part way through the week', spends: [1400, 900] },
  { label: 'Nearly empty — normal, not a warning', spends: [1400, 900, 2600, 800] },
  { label: 'Exactly zero', spends: [6000] },
  { label: 'Overdrawn — borrowed from next week', spends: [6000, 800] },
  { label: 'More than a full tank (nothing rolling it out)', spends: [], extraRefills: true },
  { label: 'Badly overdrawn', spends: [6000, 5200] },
];

const TILES = [
  { id: 't1', label: 'Ice cream', defaultAmountCents: 600 },
  { id: 't2', label: 'Pizza', defaultAmountCents: 2500 },
  { id: 't3', label: 'Shakes', defaultAmountCents: 1200 },
  { id: 't4', label: 'Store run', defaultAmountCents: 1000 },
];

/**
 * The real loop, with an array standing in for the database.
 *
 * Worth having as its own thing: it exercises tile → spendEntry →
 * projectEnvelope → gauge, and undo → envelopeReversalEntry → gauge, which is
 * every part of the fifteen-second path except the network. If undo stops
 * giving the money back, this is where it shows.
 */
function LiveLoop() {
  const [entries, setEntries] = useState(() => refillEntries({
    familyId: FAMILY, envelopeId: ENVELOPE, rule: RULE,
    fromDate: '2026-08-16', toDate: TODAY,
  }));
  const projection = projectEnvelope(entries);

  return (
    <div className="stack">
      <FuelGauge
        balanceCents={projection.balanceCents}
        refillCents={RULE.amountCents}
        cadence={RULE.cadence}
        today={TODAY}
        goal={GOAL}
      />
      <QuickLog
        tiles={TILES}
        onLog={async (tile, amountCents, note) => {
          const entry = spendEntry({
            familyId: FAMILY, envelopeId: ENVELOPE, spendId: crypto.randomUUID(),
            amountCents, tileId: tile?.id ?? null, note, occurredAt: TODAY,
          });
          setEntries((e) => [...e, entry]);
          return entry;
        }}
        onUndo={async (entry) => {
          setEntries((e) => [...e, envelopeReversalEntry({
            original: entry, actorId: 'preview', reason: 'Logged by mistake', occurredAt: TODAY,
          })]);
        }}
      />
      <p className="small muted">
        Balance {projection.balanceCents}¢ from {entries.length}{' '}
        {entries.length === 1 ? 'entry' : 'entries'}
        {projection.reversedCents ? ` · ${projection.reversedCents}¢ reversed` : ''}
      </p>
    </div>
  );
}

export default function GaugePreview() {
  return (
    <main className="app-shell stack">
      <h1>Family Fuel</h1>

      <p className="eyebrow">Log and undo, against the real domain</p>
      <LiveLoop />

      <h2>Setting it up</h2>
      <FuelSetupForm onSubmit={(v) => console.log('setup', v)} busy={false} error={null} />

      <h2>Gauge states</h2>
      <p className="muted small">
        Built from real entries through <code>projectEnvelope</code>. If the domain and the
        gauge disagree, it shows up here first.
      </p>

      {CASES.map((c) => {
        const p = scenario(c.spends, c.extraRefills);
        return (
          <div className="stack tight" key={c.label}>
            <p className="eyebrow">{c.label}</p>
            <FuelGauge
              balanceCents={p.balanceCents}
              refillCents={RULE.amountCents}
              cadence={RULE.cadence}
              today={TODAY}
              goal={GOAL}
            />
          </div>
        );
      })}

      <div className="stack tight">
        <p className="eyebrow">No rule yet</p>
        <FuelGauge balanceCents={0} refillCents={0} today={TODAY} onSetUp={() => {}} />
      </div>

      <div className="stack tight">
        <p className="eyebrow">No goal set</p>
        <FuelGauge balanceCents={4600} refillCents={6000} today={TODAY} />
      </div>
    </main>
  );
}
